import { useEffect, useMemo, useRef, useState } from 'react'
import { theme } from '../theme'
import { api, RuleValidationError, type RuleWriteBody } from '../api'
import type { Rule, RuleKind, RuleAction, RuleValidation } from '../types'
import { NavLink } from './AgentDocViewer'

// RulesEditor renders the Advanced "Planning rules" panel inside the Preferences tab.
// Three concerns:
//   - List the user's visible rules (system + user + agent) with toggle and
//     delete (delete only for user/agent; system is toggle-only).
//   - Author a new rule via the raw DSL form with live validation.
//   - Edit an existing user/agent rule with the same form.
//
// Rule data is owned by App.tsx — we receive the current list as a prop and
// rely on the SSE patch stream to deliver our own writes back. That keeps
// state consistent with other surfaces and matches the pattern used by the
// slot mask and meal data.

export function RulesEditor({ rules }: { rules: Rule[] }) {
  const [editor, setEditor] = useState<EditorState | null>(null)

  const grouped = useMemo(() => {
    const sys: Rule[] = []
    const user: Rule[] = []
    const agent: Rule[] = []
    for (const r of rules) {
      if (r.source === 'system') sys.push(r)
      else if (r.source === 'agent') agent.push(r)
      else user.push(r)
    }
    return { sys, user, agent }
  }, [rules])

  return (
    <div>
      <div style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 14, lineHeight: 1.5 }}>
        Rules tell the planner what to bias toward or avoid. The chat agent
        can write rules for you (just say "don't plan pasta this week" or
        "tacos every Tuesday"); the editor here is for hand-tuning.
      </div>

      <RuleGroup title="System rules" subtitle="Shipped defaults — you can disable but not delete." rules={grouped.sys}
        canEdit={false} canDelete={false} onEdit={() => {}} />
      <RuleGroup title="Your rules" subtitle={grouped.user.length ? '' : 'No custom rules yet.'} rules={grouped.user}
        canEdit canDelete onEdit={r => setEditor({ mode: 'edit', rule: r })} />
      {grouped.agent.length > 0 && (
        <RuleGroup title="Agent-written" subtitle="Rules the chat agent saved on your behalf." rules={grouped.agent}
          canEdit canDelete onEdit={r => setEditor({ mode: 'edit', rule: r })} />
      )}

      <div style={{ marginTop: 18 }}>
        <button
          onClick={() => setEditor({ mode: 'create' })}
          style={primaryBtn}
        >
          + Add rule
        </button>
      </div>

      {editor && (
        <RuleFormModal
          initial={editor.mode === 'edit' ? editor.rule : undefined}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  )
}

type EditorState =
  | { mode: 'create' }
  | { mode: 'edit'; rule: Rule }

// ---- list group -----------------------------------------------------------

function RuleGroup({ title, subtitle, rules, canEdit, canDelete, onEdit }: {
  title: string
  subtitle?: string
  rules: Rule[]
  canEdit: boolean
  canDelete: boolean
  onEdit: (r: Rule) => void
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontFamily: theme.sans, fontSize: 11, letterSpacing: 1.2,
        textTransform: 'uppercase', color: theme.muted, fontWeight: 600,
        marginBottom: 6,
      }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 6 }}>{subtitle}</div>
      )}
      {rules.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rules.map(r => (
            <RuleRow key={r.id} rule={r} canEdit={canEdit} canDelete={canDelete} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  )
}

function RuleRow({ rule, canEdit, canDelete, onEdit }: {
  rule: Rule
  canEdit: boolean
  canDelete: boolean
  onEdit: (r: Rule) => void
}) {
  const [busy, setBusy] = useState(false)
  const toggle = async () => {
    if (busy) return
    setBusy(true)
    try { await api.toggleRule(rule.id, !rule.enabled) }
    catch (e) { console.error(e) }
    finally { setBusy(false) }
  }
  const del = async () => {
    if (busy) return
    if (!window.confirm(`Delete rule "${rule.name}"? This can't be undone.`)) return
    setBusy(true)
    try { await api.deleteRule(rule.id) }
    catch (e) { console.error(e) }
    finally { setBusy(false) }
  }
  return (
    <div style={{
      border: `1px solid ${theme.line}`, borderRadius: theme.radius,
      background: theme.surface, padding: '10px 12px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      opacity: rule.enabled ? 1 : 0.6,
    }}>
      <button
        onClick={toggle}
        disabled={busy}
        aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
        style={{
          all: 'unset', cursor: busy ? 'default' : 'pointer',
          width: 28, height: 28, display: 'grid', placeItems: 'center',
          borderRadius: theme.radius, marginTop: 1,
          background: rule.enabled ? theme.ink : theme.surface,
          color: rule.enabled ? theme.surface : theme.muted,
          border: `1px solid ${rule.enabled ? theme.ink : theme.line}`,
          fontSize: 12, fontWeight: 600,
        }}
      >{rule.enabled ? '●' : '○'}</button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: theme.sans, fontSize: 14, fontWeight: 600, color: theme.ink }}>
            {rule.name}
          </span>
          <span style={pill}>{rule.kind}{rule.action ? ` · ${rule.action.replace('_', ' ')}` : ''}</span>
          {rule.effective_from || rule.effective_through ? (
            <span style={pill}>
              {rule.effective_from ?? '…'} → {rule.effective_through ?? '…'}
            </span>
          ) : null}
        </div>
        <RuleSummary rule={rule} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        {canEdit && (
          <button onClick={() => onEdit(rule)} style={smallBtn}>Edit</button>
        )}
        {canDelete && (
          <button onClick={del} disabled={busy} style={smallBtn}>Delete</button>
        )}
      </div>
    </div>
  )
}

// RuleSummary collapses the raw DSL into a compact preview, falling back to
// the expressions verbatim — the editor is the canonical place to see/edit them.
function RuleSummary({ rule }: { rule: Rule }) {
  return (
    <div style={{ fontSize: 12, color: theme.inkSoft, marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
      <div><span style={kw}>scope</span> {rule.scope_expr}</div>
      <div><span style={kw}>match</span> {rule.predicate_expr}</div>
      {rule.kind === 'weight' && rule.multiplier_expr && (
        <div><span style={kw}>× </span>{rule.multiplier_expr}</div>
      )}
    </div>
  )
}

// ---- form modal -----------------------------------------------------------

function RuleFormModal({ initial, onClose }: {
  initial?: Rule
  onClose: () => void
}) {
  const editing = initial != null
  const [name, setName] = useState(initial?.name ?? '')
  const [kind, setKind] = useState<RuleKind>(initial?.kind ?? 'filter')
  const [action, setAction] = useState<RuleAction>(initial?.action ?? 'include_only')
  const [scope, setScope] = useState(initial?.scope_expr ?? 'true')
  const [predicate, setPredicate] = useState(initial?.predicate_expr ?? '')
  const [mult, setMult] = useState(initial?.multiplier_expr ?? '')
  const [from, setFrom] = useState(initial?.effective_from ?? '')
  const [through, setThrough] = useState(initial?.effective_through ?? '')
  const [errors, setErrors] = useState<RuleValidation['errors']>([])
  const [saving, setSaving] = useState(false)

  // Debounced live validation — gives the user fast feedback without
  // pounding the validate endpoint on every keystroke.
  const debounceRef = useRef<number | null>(null)
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      const body = buildBody(name, kind, action, scope, predicate, mult, from, through)
      try {
        const vr = await api.validateRule(body)
        setErrors(vr.ok ? [] : vr.errors)
      } catch { /* network blip — silent */ }
    }, 400)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [name, kind, action, scope, predicate, mult, from, through])

  const errFor = (field: string) => errors.find(e => e.field === field)?.message

  const save = async () => {
    if (saving) return
    setSaving(true)
    const body = buildBody(name, kind, action, scope, predicate, mult, from, through)
    try {
      if (editing) await api.updateRule(initial!.id, body)
      else await api.createRule(body)
      onClose()
    } catch (e) {
      if (e instanceof RuleValidationError) {
        setErrors(e.result.errors)
      } else {
        console.error(e)
        setErrors([{ field: 'expression', message: String(e) }])
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,20,20,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.bg, borderRadius: theme.radiusLg,
          maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto',
          padding: 24, border: `1px solid ${theme.line}`,
        }}
      >
        <div style={{
          fontFamily: theme.serif, fontWeight: theme.headWeight,
          fontSize: 22, color: theme.ink, marginBottom: 4,
        }}>{editing ? 'Edit rule' : 'New rule'}</div>
        <div style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 16, lineHeight: 1.5 }}>
          You're strongly encouraged to let the chat agent manage your meal-planning
          rules — it speaks this DSL fluently and can talk through tradeoffs with you.
          If you'd really like to understand the planning engine in detail, you can
          read the same documentation the agent sees{' '}
          <NavLink href="/agent-docs/rules-dsl" style={{ textDecoration: 'underline' }}>
            here
          </NavLink>.
        </div>

        <Field label="Name" error={errFor('name')}>
          <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="e.g. Taco Tuesday" />
        </Field>

        <Field label="Kind">
          <div style={{ display: 'flex', gap: 8 }}>
            <PillBtn active={kind === 'filter'} onClick={() => setKind('filter')}>Filter</PillBtn>
            <PillBtn active={kind === 'weight'} onClick={() => setKind('weight')}>Weight</PillBtn>
          </div>
        </Field>

        {kind === 'filter' && (
          <Field label="Action">
            <div style={{ display: 'flex', gap: 8 }}>
              <PillBtn active={action === 'include_only'} onClick={() => setAction('include_only')}>Include only</PillBtn>
              <PillBtn active={action === 'exclude'} onClick={() => setAction('exclude')}>Exclude</PillBtn>
            </div>
          </Field>
        )}

        <Field label="Scope (when this rule fires)" error={errFor('scope_expr')}
          hint="Boolean over the slot context — try 'true' for always-on.">
          <textarea value={scope} onChange={e => setScope(e.target.value)} rows={2} style={mono} />
        </Field>

        <Field label="Match predicate" error={errFor('predicate_expr')}
          hint="Boolean over (slot, recipe). E.g. recipe.tags.dish_type contains 'taco'">
          <textarea value={predicate} onChange={e => setPredicate(e.target.value)} rows={3} style={mono} />
        </Field>

        {kind === 'weight' && (
          <Field label="Multiplier" error={errFor('multiplier_expr')}
            hint="Number expression. 3.0 to triple, 0.5 to halve. Clamped to [0, 1000].">
            <textarea value={mult} onChange={e => setMult(e.target.value)} rows={2} style={mono} />
          </Field>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <Field label="Effective from (optional)">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={input} />
          </Field>
          <Field label="Effective through (optional)">
            <input type="date" value={through} onChange={e => setThrough(e.target.value)} style={input} />
          </Field>
        </div>

        {errors.length > 0 && (
          <div style={{
            padding: '8px 10px', borderRadius: theme.radius,
            background: '#fbecec', color: '#7a1f1f', fontSize: 13, marginBottom: 14,
          }}>
            {errors.map((e, i) => (
              <div key={i}><strong>{e.field}:</strong> {e.message}</div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button
            onClick={save}
            disabled={saving || errors.length > 0 || name.trim() === '' || predicate.trim() === ''}
            style={primaryBtn}
          >
            {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  )
}

function buildBody(
  name: string, kind: RuleKind, action: RuleAction,
  scope: string, predicate: string, mult: string,
  from: string, through: string,
): RuleWriteBody {
  const body: RuleWriteBody = {
    name: name.trim(),
    kind,
    scope_expr: scope,
    predicate_expr: predicate,
  }
  if (kind === 'filter') body.action = action
  if (kind === 'weight') body.multiplier_expr = mult
  if (from) body.effective_from = from
  if (through) body.effective_through = through
  return body
}

// ---- bits -----------------------------------------------------------------

function Field({ label, hint, error, children }: {
  label: string
  hint?: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 14, flex: 1 }}>
      <div style={{
        fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
        color: theme.muted, fontWeight: 600, marginBottom: 4,
      }}>{label}</div>
      {children}
      {hint && !error && (
        <div style={{ fontSize: 12, color: theme.inkSoft, marginTop: 4 }}>{hint}</div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: '#a02828', marginTop: 4 }}>{error}</div>
      )}
    </div>
  )
}

function PillBtn({ active, onClick, children }: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button onClick={onClick} style={{
      all: 'unset', cursor: 'pointer',
      padding: '8px 14px', borderRadius: theme.radius,
      border: `1px solid ${active ? theme.ink : theme.line}`,
      background: active ? theme.ink : theme.surface,
      color: active ? theme.surface : theme.ink,
      fontFamily: theme.sans, fontSize: 13, fontWeight: 500,
    }}>{children}</button>
  )
}

const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  padding: '8px 10px', borderRadius: theme.radius,
  border: `1px solid ${theme.line}`,
  background: theme.surface, color: theme.ink,
  fontFamily: theme.sans, fontSize: 14,
}

const mono: React.CSSProperties = {
  ...input,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13, lineHeight: 1.45, resize: 'vertical',
}

const primaryBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '10px 16px', borderRadius: theme.radius,
  background: theme.ink, color: theme.surface,
  fontFamily: theme.sans, fontSize: 14, fontWeight: 500,
}

const ghostBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '10px 16px', borderRadius: theme.radius,
  background: theme.surface, color: theme.ink,
  border: `1px solid ${theme.line}`,
  fontFamily: theme.sans, fontSize: 14, fontWeight: 500,
}

const smallBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '4px 10px', borderRadius: theme.radius,
  background: theme.surface, color: theme.inkSoft,
  border: `1px solid ${theme.line}`,
  fontFamily: theme.sans, fontSize: 12,
}

const pill: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px', borderRadius: 999,
  background: theme.bg, color: theme.muted,
  fontSize: 11, fontWeight: 500, letterSpacing: 0.3,
  border: `1px solid ${theme.line}`,
}

const kw: React.CSSProperties = {
  color: theme.muted, fontWeight: 600, marginRight: 6,
}
